/**
 * IMAGE STYLE ATLAS — v1.4
 * 102 named visual styles for album cover art.
 * Source: ukslim "Poster Prompts" (100 styles) + 2 house originals
 * (Golden Hour Photo-Real, FMR Records).
 * De-postered and rebuilt for album-art use per §8.5 external-spec doctrine.
 * See also: buildPresetAtlasEntry() — synthesizes additional "Style Presets"
 * category entries live from the user's saved Style Presets, not part of
 * this curated count.
 */

export type PackagePreset = 'pkg_vinyl_classic' | 'pkg_vinyl_80s' | 'pkg_cd_era' | 'pkg_modern_print';

export interface StyleEntry {
  id: string;
  number: number;
  name: string;
  category: string;
  era: string;
  mood: string;
  palette: string;
  traits: string;
  references: string;
  exclude: string;
  caution?: string;
  /** True when the EXCLUDE list bans aging language — forces pkg_modern_print */
  excludesAging: boolean;
  /** Recommended package realism preset per §2A aging-check table */
  packagePreset: PackagePreset;
}

export const ATLAS_CATEGORIES = [
  'Graphic Design Movements',
  'Fine Art Movements',
  'Print Techniques',
  'Folk & Vernacular',
  'National & Regional',
  'Political & Activist',
  'Music Scenes',
  'Digital & Screen',
  'Style Presets',
] as const;

export const PACKAGE_PRESETS: Record<PackagePreset, { label: string; clause: string; labelImprint: string }> = {
  pkg_vinyl_classic: {
    label: 'Vinyl LP (1960s–70s)',
    clause: 'matte cardboard sleeve texture, slight vinyl ring-wear circle, soft corner shelf wear, subtle film grain, photographed print reproduction — not a flat digital vector',
    labelImprint: 'small record-label imprint in the upper-left corner: house label wordmark "OCMULGEE SOUND," the word "STEREO," and a 4-digit catalog number (e.g. "OS 1076") in period-correct small serif type, plus a simple monogram mark beneath',
  },
  pkg_vinyl_80s: {
    label: 'Vinyl / Cassette (1980s)',
    clause: 'glossy or satin cardboard sleeve texture, light shelf wear at corners and edges, faint print-registration softness, photographed print reproduction',
    labelImprint: 'same imprint block, upper-left, but in a bolder 1980s sans-serif with the catalog number in a boxed border',
  },
  pkg_cd_era: {
    label: 'CD Era (1990s–2000s)',
    clause: 'printed jewel-case insert texture, subtle case-corner crease, slight paper-stock sheen, photographed/scanned print reproduction',
    labelImprint: 'small printed spine/insert text along one edge: house label name, catalog number, a thin barcode suggestion — kept subtle, not a hero element',
  },
  pkg_modern_print: {
    label: 'Modern Print',
    clause: 'clean modern print stock, no aging or wear, matte or satin finish per style, photographed print reproduction — not a flat digital vector',
    labelImprint: 'minimal modern label mark, lower corner, small and unobtrusive — a wordmark only, no catalog number needed',
  },
};

export const PHYSICAL_EDGE_CLAUSE = 'slight visible sleeve edge or border consistent with a physical printed cover — not a full-bleed digital crop';

/** House-default style picks per recurring project persona (§3 of the atlas) */
export const HOUSE_DEFAULTS: Record<string, { primary: string; alternates: string[] }> = {
  'Southern Americana': {
    primary: 'neon_sign',
    alternates: ['screenprint_pop', 'halftone_newsprint', 'etching_engraving', 'botanical_scientific', 'golden_hour_photoreal'],
  },
  'Scooby VanZant': {
    primary: 'heavy_metal_poster',
    alternates: ['grunge_typography', 'brutalist_graphic', 'expressionist_woodcut'],
  },
  'Dark Cinematic': {
    primary: 'german_expressionist_film',
    alternates: ['polish_poster_school', 'monochrome_single_accent', 'penny_dreadful'],
  },
  'Psychedelic / Jam': {
    primary: 'psychedelic_sixties',
    alternates: ['synthwave', 'vaporwave'],
  },
};

export const IMAGE_STYLE_ATLAS: StyleEntry[] = [
  // ─── Graphic Design Movements ────────────────────────────────────────────────
  {
    id: 'arts_and_crafts', number: 1, name: 'Arts and Crafts',
    category: 'Graphic Design Movements', era: '1880s–1910s', mood: 'earnest',
    palette: 'forest green, terracotta, ochre, deep burgundy, cream',
    traits: 'stylised natural forms — vines, flowers, birds; hand-crafted texture suggesting woodcut or embroidery; symmetrical or balanced ornamental composition; muted earth tones with rich accent colours',
    references: 'William Morris, CFA Voysey',
    exclude: 'pastel craft-fair palette, bunting, clip-art florals',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'art_nouveau', number: 2, name: 'Art Nouveau',
    category: 'Graphic Design Movements', era: '1890s–1910s', mood: 'elegant',
    palette: 'muted gold, sage, dusty rose, teal, cream',
    traits: 'sinuous whiplash curves; ornamental borders and frames; flat colour areas with fine linework; female figure or floral motif as centrepiece',
    references: 'Alphonse Mucha, Henri de Toulouse-Lautrec, Jules Chéret',
    exclude: 'geometric modernism, sans-serif dominance, flat corporate illustration',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'art_deco', number: 3, name: 'Art Deco',
    category: 'Graphic Design Movements', era: '1920s–1930s', mood: 'glamorous',
    palette: 'black, gold, cream, deep teal, coral',
    traits: 'geometric symmetry and stepped forms; sunburst or fan motifs; metallic gold or silver accents; stylised figures or architecture; strong vertical emphasis',
    references: 'A.M. Cassandre, Erté',
    exclude: 'hand-drawn naivety, grunge texture, pastel craft aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'streamline_moderne', number: 4, name: 'Streamline Moderne',
    category: 'Graphic Design Movements', era: '1930s–1940s', mood: 'optimistic',
    palette: 'chrome silver, navy, white, red accent, sky blue',
    traits: 'horizontal speed lines; rounded aerodynamic forms; metallic sheen suggestions; dynamic forward motion; simplified industrial shapes',
    references: 'Raymond Loewy, Norman Bel Geddes',
    exclude: 'ornamental Art Nouveau curves, hand-drawn texture, static centred composition',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'bauhaus_modernist', number: 5, name: 'Bauhaus / Modernist',
    category: 'Graphic Design Movements', era: '1920s–1930s', mood: 'cultural',
    palette: 'primary red, blue, yellow, black, white',
    traits: 'primary colour blocks — red, blue, yellow; basic geometric shapes — circles, rectangles, semicircles; flat colour with no texture or shading; abstracted symbolic forms rather than literal illustration',
    references: 'Herbert Bayer, László Moholy-Nagy, Josef Albers',
    exclude: 'pastel florals, decorative borders, bunting, hand-drawn craft aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'swiss_international', number: 6, name: 'Swiss / International Typographic Style',
    category: 'Graphic Design Movements', era: '1950s–1970s', mood: 'institutional',
    palette: 'white, black, one accent colour — red, orange, or green',
    traits: 'mathematical grid alignment; large areas of white or single-colour field; photographic or abstract graphic element; minimal decoration',
    references: 'Josef Müller-Brockmann, Armin Hofmann, Emil Ruder',
    exclude: 'ornamental decoration, multiple competing colours, hand-drawn elements, craft-fair aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'constructivist', number: 7, name: 'Constructivist',
    category: 'Graphic Design Movements', era: '1920s–1930s', mood: 'urgent',
    palette: 'red, black, white, occasional grey',
    traits: 'diagonal axes and wedge shapes; photomontage or halftone collage; red and black dominance; radiating lines suggesting movement; bold geometric abstraction',
    references: 'Alexander Rodchenko, El Lissitzky',
    exclude: 'soft pastels, symmetrical decorative layouts, literal illustration',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'de_stijl', number: 8, name: 'De Stijl',
    category: 'Graphic Design Movements', era: '1917–1930s', mood: 'intellectual',
    palette: 'red, blue, yellow, black, white only',
    traits: 'horizontal and vertical lines only — no diagonals; rectangles in primary colours; black dividing lines; asymmetric balance through colour weight',
    references: 'Piet Mondrian, Theo van Doesburg',
    exclude: 'curves or organic shapes, gradients or shadows, decorative type',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'mid_century_modern_graphic', number: 9, name: 'Mid-century Modern Graphic',
    category: 'Graphic Design Movements', era: '1940s–1960s', mood: 'optimistic',
    palette: 'teal, mustard, coral, avocado, cream, charcoal',
    traits: 'atomic or starburst motifs; simplified organic shapes — kidneys, boomerangs; flat colour illustration; playful asymmetry',
    references: 'Paul Rand, Alvin Lustig, Saul Bass',
    exclude: 'grunge or distress, dark moody palettes, craft-fair hand-drawn florals',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'memphis_postmodern', number: 10, name: 'Memphis / 80s Postmodern',
    category: 'Graphic Design Movements', era: '1980s', mood: 'fun',
    palette: 'hot pink, electric blue, yellow, black, white, teal',
    traits: 'squiggles and zigzag patterns; clashing bright colours; laminated or plastic surface feel; mix of geometric and organic shapes; terrazzo or speckled textures',
    references: 'Ettore Sottsass, Memphis Group',
    exclude: 'muted natural palettes, serious minimalism, craft-fair aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_80s',
  },
  {
    id: 'brutalist_graphic', number: 11, name: 'Brutalist Graphic Design',
    category: 'Graphic Design Movements', era: '1960s–present', mood: 'rebellious',
    palette: 'black, white, one brutal accent — orange or yellow',
    traits: 'large black rectangular fields; extreme contrast — black on white or white on black; raw unpolished edges; minimal or no illustration; concrete-grey textures optional',
    references: 'Experimental Jetset, Brutalist architecture tradition',
    exclude: 'soft pastels, decorative illustration, craft-fair warmth',
    caution: 'Tone down slightly for family events — keep raw energy without illegibility',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'contemporary_editorial', number: 12, name: 'Contemporary Editorial',
    category: 'Graphic Design Movements', era: '2000s–present', mood: 'premium',
    palette: 'white, black, one editorial accent; muted photography tones',
    traits: 'large headline dominating upper portion; high-quality photography or illustration area; generous margins; subtle colour blocking',
    references: 'The New York Times Magazine, Monocle',
    exclude: 'clip-art, bunting, multiple decorative fonts',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'wayfinding_signage', number: 13, name: 'Wayfinding / Signage',
    category: 'Graphic Design Movements', era: '1960s–present', mood: 'functional',
    palette: 'white, dark grey, safety green, directional blue, alert orange',
    traits: 'simple icons with consistent stroke weight; directional arrows; colour-coded zones or categories; pictograms for activities; map-like spatial organisation',
    references: 'London Underground signage, National Park Service graphics',
    exclude: 'decorative illustration, script fonts, craft-fair ornament',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'vienna_secession', number: 14, name: 'Vienna Secession',
    category: 'Graphic Design Movements', era: '1897–1914', mood: 'jewelled',
    palette: 'black, white, gold, grey-violet, muted rose, deep teal',
    traits: 'rectilinear grid frames containing organic or figurative motifs; gold leaf or metallic gold as primary design element; stylised geometric flower heads — squares within circles; high-contrast black and white with gold and muted colour accents; bilateral symmetry with decorative insets',
    references: 'Gustav Klimt, Josef Hoffmann, Koloman Moser, Ver Sacrum magazine',
    exclude: 'flowing naturalistic curves of French Art Nouveau, pastel softness, craft-fair warmth, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'glasgow_style', number: 15, name: 'Glasgow Style',
    category: 'Graphic Design Movements', era: '1893–1913', mood: 'cool',
    palette: 'black, white, rose pink, grey-purple, silver-green',
    traits: 'extreme vertical emphasis — tall narrow compositions; elongated attenuated human figures or organic forms; stylised rose or abstract floral motif; strong black outlines on white with limited colour; grid insets and square decorative elements',
    references: 'Charles Rennie Mackintosh, Margaret Macdonald, Frances Macdonald, Herbert McNair',
    exclude: 'warm naturalistic organic curves of French Art Nouveau, gold and jewelled richness of Vienna Secession, craft-fair pastels, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'push_pin_studios', number: 16, name: 'Push Pin Studios',
    category: 'Graphic Design Movements', era: '1954–1990s', mood: 'witty',
    palette: 'flat vivid — deep cobalt, vermillion, lime green, gold, black',
    traits: 'flat black silhouette of figure or portrait profile as primary element; hair, clothing, or background filled with swirling Art Nouveau line patterns; all colour areas flat — no gradient, no shading, no blending; strong black outline defining every form; limited palette of vivid flat colours',
    references: 'Milton Glaser (Bob Dylan poster 1966), Seymour Chwast, Push Pin Monthly Graphic',
    exclude: 'photorealistic illustration or airbrush rendering, gradient or blended colour anywhere, generic corporate vector illustration, Swiss-style grid, artificial yellowing or ageing',
    caution: 'Specify "flat black silhouette profile, Art Nouveau swirling hair pattern, pure flat colour blocking, no gradients, in the style of Milton Glaser\'s 1966 Bob Dylan poster"',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'new_wave_typography', number: 17, name: 'New Wave Typography',
    category: 'Graphic Design Movements', era: '1976–1990', mood: 'intellectual',
    palette: 'black, white, warm grey; one or two strong accent colours — red, yellow, or blue',
    traits: 'fragmented overlapping grid columns; photographic elements combined with type and geometry; halftone dot screens used as visual texture; text blocks at angles cutting across the composition; layered transparent elements creating depth',
    references: 'Wolfgang Weingart, April Greiman, Dan Friedman',
    exclude: 'clean Swiss orthodoxy, decorative illustration, craft-fair aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'grunge_typography', number: 18, name: 'Grunge Typography',
    category: 'Graphic Design Movements', era: '1990–1998', mood: 'raw',
    palette: 'black, white, and photographic tones; occasional crude colour',
    traits: 'damaged or distorted typefaces; text overprinting images or running off the edge; torn paper, scratched film, or photocopied textures; no clear typographic hierarchy; image and text compete for the same space',
    references: 'David Carson, Ray Gun magazine, Ed Fella',
    exclude: 'Swiss-style order, clean digital polish, craft-fair aesthetics, artificial yellowing or ageing',
    caution: 'Legibility suffers by design — keep event essentials findable despite the chaos',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Fine Art Movements ───────────────────────────────────────────────────────
  {
    id: 'japonisme', number: 19, name: 'Japonisme',
    category: 'Fine Art Movements', era: '1870s–1900s', mood: 'refined',
    palette: 'indigo, vermillion, cream, black, muted grey-green',
    traits: 'flat colour planes without shading; asymmetrical composition; cropped or off-centre focal element; bold outlines around forms; negative space used actively',
    references: 'Ukiyo-e print tradition, James McNeill Whistler',
    exclude: 'busy symmetrical layouts, gradient shading, ornate Victorian decoration',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'victorian_broadside', number: 20, name: 'Victorian Broadside',
    category: 'Fine Art Movements', era: '1850s–1900s', mood: 'boisterous',
    palette: 'black, cream, deep red, gold, forest green',
    traits: 'multiple woodtype sizes stacked tightly; ornamental rules and dividers; central woodcut or engraving illustration; border frames',
    references: 'Victorian playbill tradition, Pre-Raphaelite colour sensibility',
    exclude: 'modern sans-serif, minimal whitespace, flat vector illustration',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'belle_epoque_lithograph', number: 21, name: 'Belle Époque Lithograph',
    category: 'Fine Art Movements', era: '1890s–1910s', mood: 'lively',
    palette: 'warm red, yellow, black, teal, cream',
    traits: 'lithographic colour layering; lively figure in motion; spontaneous brushstroke energy; limited palette per stone',
    references: 'Jules Chéret, Henri de Toulouse-Lautrec',
    exclude: 'digital flatness without texture, geometric modernism',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'futurist', number: 22, name: 'Futurist',
    category: 'Fine Art Movements', era: '1909–1940s', mood: 'aggressive',
    palette: 'red, black, grey, yellow, white',
    traits: 'force lines and motion blur; fragmented overlapping forms; diagonal composition; mechanical and urban imagery',
    references: 'F.T. Marinetti, Giacomo Balla',
    exclude: 'static centred layouts, soft pastoral imagery, craft aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'dada_collage', number: 23, name: 'Dada Collage',
    category: 'Fine Art Movements', era: '1916–1920s', mood: 'absurdist',
    palette: 'black, white, newsprint grey, one red accent',
    traits: 'mixed media collage — photographs, paper scraps, type; absurd scale relationships; monochrome with one shock accent; visible cut edges and overlaps',
    references: 'Hannah Höch, Kurt Schwitters, Marcel Duchamp',
    exclude: 'polished corporate layout, harmonious colour schemes',
    caution: 'Illegibility risk — keep event details readable despite chaos',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'cubist_exhibition', number: 24, name: 'Cubist Exhibition Poster',
    category: 'Fine Art Movements', era: '1940s framing (Cubism 1907–1920s)', mood: 'intellectual',
    palette: 'ochre, grey, umber, black, cream',
    traits: 'fragmented geometric planes; multiple viewpoints in one image; muted ochre, grey, and umber palette; analytical line structure; abstracted still-life or figure forms',
    references: 'Pablo Picasso, Georges Braque',
    exclude: 'bright pop colours, literal realistic illustration, craft-fair aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'surrealist', number: 25, name: 'Surrealist',
    category: 'Fine Art Movements', era: '1920s–1950s', mood: 'uncanny',
    palette: 'deep blue sky, warm sand, muted green, realistic tones',
    traits: 'impossible juxtapositions — scale shifts, floating objects; hyper-real rendering of unreal scenes; vast empty landscapes or skies; symbolic objects — clocks, apples, birds',
    references: 'Salvador Dalí, René Magritte',
    exclude: 'cartoon simplification, busy decorative borders, craft-fair warmth',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'expressionist_woodcut', number: 26, name: 'Expressionist Woodcut',
    category: 'Fine Art Movements', era: '1905–1920s', mood: 'raw',
    palette: 'black, white, one accent — red or orange',
    traits: 'bold woodcut or linocut texture; jagged angular forms; high contrast black and white; distorted perspective for emotional effect; visible carving marks',
    references: 'Käthe Kollwitz, Die Brücke, Emil Nolde',
    exclude: 'soft pastels, smooth vector gradients, decorative ornament',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'pop_art', number: 27, name: 'Pop Art',
    category: 'Fine Art Movements', era: '1950s–1960s', mood: 'loud',
    palette: 'red, yellow, blue, black, white',
    traits: 'Ben-Day dot patterns; bold black outlines around colour fields; comic-book halftone; consumer product or celebrity reference; flat primaries',
    references: 'Andy Warhol, Roy Lichtenstein, Richard Hamilton',
    exclude: 'muted natural palettes, fine-art restraint, craft aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'op_art', number: 28, name: 'Op Art',
    category: 'Fine Art Movements', era: '1960s', mood: 'hypnotic',
    palette: 'black, white, occasional single accent',
    traits: 'concentric circles or parallel lines creating vibration; black and white high contrast; geometric repetition; illusion of movement or depth',
    references: 'Bridget Riley, Victor Vasarely',
    exclude: 'illustrative scenes, soft organic forms, busy multi-font typography',
    caution: 'Pattern can overwhelm — reserve a quiet zone for cover text',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'psychedelic_sixties', number: 29, name: 'Psychedelic Sixties',
    category: 'Fine Art Movements', era: '1966–1970s', mood: 'trippy',
    palette: 'hot pink, orange, purple, lime green, electric blue',
    traits: 'swirling organic forms; vibrating colour combinations; Art Nouveau-influenced letterforms; dense all-over composition',
    references: 'Wes Wilson, Victor Moscoso, Rick Griffin',
    exclude: 'minimal whitespace, corporate sans-serif, craft-fair pastels',
    caution: 'Legibility suffers by design — ensure cover text remains findable',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'minimalism_art', number: 30, name: 'Minimalism (Art)',
    category: 'Fine Art Movements', era: '1960s–1970s', mood: 'contemplative',
    palette: 'white, off-white, one colour — red or yellow',
    traits: 'single geometric form or line; vast empty field; no decoration whatsoever; industrial precision',
    references: 'Donald Judd, Agnes Martin, Dan Flavin',
    exclude: 'illustration, multiple colours, decorative type',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'graffiti_street_art', number: 31, name: 'Graffiti / Street Art',
    category: 'Fine Art Movements', era: '1970s–present', mood: 'urban',
    palette: 'electric colours on dark or brick background',
    traits: 'spray paint texture and drips; bold handstyle lettering; brick wall or urban surface background; stencil layers; vibrant fill colours with black outlines',
    references: 'Jean-Michel Basquiat, Banksy, Keith Haring',
    exclude: 'pastoral imagery, craft-fair aesthetics, corporate polish',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'suprematism', number: 32, name: 'Suprematism',
    category: 'Fine Art Movements', era: '1915–1925', mood: 'pure',
    palette: 'black, red, white; very occasionally muted grey or blue-grey',
    traits: 'basic geometric forms — square, rectangle, circle, cross, diagonal bar; shapes float against a white or off-white ground; implied movement through rotation and diagonal placement; forms overlap or cluster; absolutely no representational imagery',
    references: 'Kazimir Malevich, Black Square (1915), UNOVIS group',
    exclude: 'representation of objects, figures, or landscapes, decorative detail, gradient or shadow, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'vorticism', number: 33, name: 'Vorticism',
    category: 'Fine Art Movements', era: '1914–1920', mood: 'aggressive',
    palette: 'black, white; sharp red or yellow as single accent',
    traits: 'diagonal wedge and triangle forms radiating from a centre; compressed overlapping angular planes; harsh black-and-white contrast with minimal colour; machine-like interlocking shapes suggesting force; no organic curves anywhere',
    references: 'Wyndham Lewis, BLAST magazine, David Bomberg, Christopher Nevinson',
    exclude: 'organic curves or naturalistic forms, soft Futurist motion blur, pastel colour, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'neue_sachlichkeit', number: 34, name: 'Neue Sachlichkeit / New Objectivity',
    category: 'Fine Art Movements', era: '1920s–1930s', mood: 'urgent',
    palette: 'black, white, photographic grey tones; deep red as political accent',
    traits: 'photomontage combining photographs with drawn or typographic elements; harsh realistic rendering without idealism; dramatic cropping and scale contrast between elements; photographic figures placed against flat colour fields; visible collage seams used expressively',
    references: 'John Heartfield, George Grosz, Otto Dix, AIZ magazine',
    exclude: 'Expressionist distortion or emotional exaggeration, decorative ornament, craft-fair warmth, artificial yellowing or ageing',
    caution: 'Political content — frame as historical aesthetic; use for events with clear non-partisan framing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'orphism', number: 35, name: 'Orphism / Simultanism',
    category: 'Fine Art Movements', era: '1912–1920s', mood: 'joyful',
    palette: 'full spectrum — red, orange, yellow, green, blue, violet in adjacent complementary arrangement',
    traits: 'circular or arc forms arranged in prismatic progressions; pure spectral colours used in adjacent complementary pairs; colour rhythms suggesting rotation and movement; no representational subject — colour is the entire content; overlapping colour discs or wedges creating optical vibration',
    references: 'Robert Delaunay, Sonia Delaunay, Simultaneous Contrasts (1913)',
    exclude: 'representational imagery, dark or sombre tones, decorative ornament, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'concrete_art', number: 36, name: 'Concrete Art',
    category: 'Fine Art Movements', era: '1930s–1970s', mood: 'intellectual',
    palette: 'systematic sequences — not arbitrary; often full spectrum in ordered steps',
    traits: 'systematic colour progressions — sequential hue or value shifts; grid-based composition with mathematical subdivision; geometric forms that are exactly what they are — no symbolic meaning; each element repeated or permuted according to a visible rule',
    references: 'Max Bill, Richard Paul Lohse, Camille Graeser, Verena Loewensberg',
    exclude: 'subjective expression or gestural marks, decorative or symbolic imagery, arbitrary colour choices, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'cobra', number: 37, name: 'CoBrA',
    category: 'Fine Art Movements', era: '1948–1951', mood: 'raw',
    palette: 'vivid unmodulated colours — cobalt, vermillion, ochre, viridian, black, white',
    traits: 'childlike or mythic figures with bold outlines; vivid flat or semi-flat colour applied with visible energy; heavy gestural line that defines form expressively; animal, mask, and totemic imagery; composition without Western academic perspective',
    references: 'Karel Appel, Asger Jorn, Constant, Pierre Alechinsky',
    exclude: 'Swiss or Bauhaus rational geometry, photographic realism, decorative polish, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'pre_raphaelite', number: 38, name: 'Pre-Raphaelite Illustration',
    category: 'Fine Art Movements', era: '1848–1900', mood: 'romantic',
    palette: 'jewel tones — deep red, emerald, cobalt, gold, ivory; no pastels',
    traits: 'richly detailed figurative scene with elaborate natural setting; jewel-toned colour — deep emerald, ruby, cobalt, gold; intricate foliage, water, or textile detail filling the background; idealised figures — women with elaborate flowing hair',
    references: 'Dante Gabriel Rossetti, John Everett Millais, Edward Burne-Jones',
    exclude: 'modernist abstraction, photographic realism without painterly detail, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'big_eyes', number: 39, name: 'Big Eyes / Keane Waif',
    category: 'Fine Art Movements', era: '1950s–1970s', mood: 'sentimental',
    palette: 'muted warm tones — ochre, sienna, raw umber, soft grey; eyes themselves vivid and glistening',
    traits: 'enormous liquid eyes occupying a third or more of the face; soft blended painting — no hard outlines, edges dissolve into background; solitary child or animal subject, frontally posed, meeting the viewer\'s gaze; plain or loosely atmospheric background',
    references: 'Margaret Keane, Keane Gallery San Francisco',
    exclude: 'hard cartoon outlines, flat vector illustration, multiple figures competing for attention, artificial yellowing or ageing',
    caution: 'Works best when the subject is specified precisely — without it the AI will default to a generic sad child',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Print Techniques ────────────────────────────────────────────────────────
  {
    id: 'risograph', number: 40, name: 'Risograph',
    category: 'Print Techniques', era: '1980s–present', mood: 'indie',
    palette: 'fluorescent pink, teal, yellow, black — pick two or three',
    traits: 'two or three spot colours only; slight misregistration between layers; visible grain and print texture; bold simplified shapes; overprint colour mixing',
    references: 'Risograph print tradition, Independent publishing aesthetic',
    exclude: 'smooth digital gradients, full-colour photographic realism, craft-fair pastels',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'stamp_letterpress_modern', number: 41, name: 'Stamp / Letterpress (Modernised)',
    category: 'Print Techniques', era: 'Contemporary', mood: 'crafted',
    palette: 'black, cream, one accent — red or green',
    traits: 'heavy ink coverage; slight impression or deboss texture; bold simplified graphic marks; limited colour — two inks maximum; stamp-like repeated motifs',
    references: 'Hatch Show Print, Modern letterpress revival',
    exclude: 'digital smoothness, pastel craft-fair palette, bunting',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'screenprint_pop', number: 42, name: 'Screenprint Pop',
    category: 'Print Techniques', era: '1960s–present', mood: 'direct',
    palette: 'bold opaque colours — no transparency effects',
    traits: 'flat spot colours — three to five layers; slight overlap where colours meet; simplified bold illustration; visible screen texture',
    references: 'Gig poster tradition, Political screenprint',
    exclude: 'photographic realism, gradient shading, craft-fair ornament',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'botanical_scientific', number: 43, name: 'Botanical Scientific Illustration',
    category: 'Print Techniques', era: 'Contemporary', mood: 'quiet',
    palette: 'black ink on cream, one green or rust accent',
    traits: 'fine pen-and-ink botanical line drawings; specimen labels or Latin names; diagrammatic layout; white or cream background; one highlight colour for emphasis',
    references: 'Botanical illustration tradition, Scientific plate engraving',
    exclude: 'colourful cartoon florals, bunting, hand-drawn craft-fair style',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'cut_paper_matisse', number: 44, name: 'Cut Paper / Matisse',
    category: 'Print Techniques', era: '1940s–present', mood: 'joyful',
    palette: 'bright blue, orange, green, pink, white background',
    traits: 'bold organic colour shapes; visible cut-paper edges; no shading or gradient within shapes; playful scale relationships; white gaps between shapes',
    references: 'Henri Matisse, Jazz cut-out series',
    exclude: 'realistic illustration, gradient shading, craft-fair pastels',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'monochrome_single_accent', number: 45, name: 'Monochrome + Single Accent',
    category: 'Print Techniques', era: 'Contemporary', mood: 'confident',
    palette: 'black, white, one accent — bright green, orange, or red',
    traits: 'no illustration or a single minimal mark; black and white dominance; one accent colour used sparingly; strong negative space',
    references: 'Swiss poster tradition, Contemporary typographic posters',
    exclude: 'illustration-heavy layouts, multiple accent colours, craft-fair decoration',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'cyanotype', number: 46, name: 'Cyanotype / Blueprint',
    category: 'Print Techniques', era: '1842–present', mood: 'scientific',
    palette: 'Prussian blue and white only; occasional very pale blue for mid-tones',
    traits: 'deep Prussian blue ground; subject rendered as white silhouette or fine white line; high contrast with no mid-tones; delicate fine detail in white against blue; slight halo or soft edge where light has scattered',
    references: 'Anna Atkins, Botanical photogram tradition, Architectural blueprint',
    exclude: 'warm colours of any kind, photographic realism with tonal range, decorative ornament, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'penny_dreadful', number: 47, name: 'Penny Dreadful / Sensation Novel Cover',
    category: 'Print Techniques', era: '1840s–1890s', mood: 'melodramatic',
    palette: 'black engraving on white with one garish tint — blood red, poison green, or gaslight blue',
    traits: 'dramatic engraved scene at peak moment of crisis or villainy; black line illustration over a single flat colour tint; figures at extreme expressive angles — swooning, menacing, fleeing; heavy black hatching for shadow areas; moonlight, candlelight, or gaslight as dramatic lighting source',
    references: 'Penny dreadful cover tradition, Varney the Vampire (1847), Spring-Heeled Jack',
    exclude: 'decorative ornamental borders, multiple colours, restrained good taste of any kind, artificial yellowing or ageing',
    caution: 'Comic potential is high — works best when the subject is played up as dramatic',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'etching_engraving', number: 48, name: 'Etching / Engraving',
    category: 'Print Techniques', era: '16th century–present', mood: 'authoritative',
    palette: 'black ink on cream or warm white; sepia-toned variant for aquatint feeling',
    traits: 'fine cross-hatching and parallel lines building tonal gradients; precise scratchy line quality suggesting a metal point; high contrast with mid-tones achieved through line density; detailed figurative or architectural imagery; slight plate tone or aquatint ground optional',
    references: 'Albrecht Dürer, Giovanni Piranesi, William Hogarth',
    exclude: 'flat vector simplification, solid colour fills, photographic realism, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'halftone_newsprint', number: 49, name: 'Halftone / Newsprint',
    category: 'Print Techniques', era: '1970s–1980s', mood: 'urgent',
    palette: 'black ink on white; optional single red for masthead banner or drop capital only',
    traits: 'large exaggerated halftone dot screen — blobs not fine dots — over all photos; photos read as high-contrast graphic patches; white or very pale grey paper ground — freshly printed, not aged; heavy black ink with slight spread; ruled column dividers and horizontal rules',
    references: 'British tabloid front page tradition — The Sun, Daily Mirror; broadsheet — The Guardian, The Times',
    exclude: 'yellowed or aged paper, colour photography other than single red accent, fine halftone dots — make them large and visible, decorative illustration, artificial yellowing or ageing',
    caution: 'Specify variant: "red-top tabloid with red masthead" or "broadsheet with formal column grid"',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'mimeograph', number: 50, name: 'Mimeograph / Ditto Machine',
    category: 'Print Techniques', era: '1920s–1980s', mood: 'community',
    palette: 'purple-blue or grey-blue on off-white paper',
    traits: 'waxy smudged purple-blue or blue-grey ink; slight ghosting or double-image on text and line work; soft unfocused edges on drawn elements; typewriter text combined with hand-drawn illustration; visible stencil grain or texture',
    references: 'Mimeograph self-publishing tradition, Community newsletter aesthetic, Early zine culture',
    exclude: 'clean digital typography, full-colour imagery, corporate polish, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Folk & Vernacular ───────────────────────────────────────────────────────
  {
    id: 'child_paint_pro_typography', number: 51, name: 'Child Poster Paint + Pro Typography',
    category: 'Folk & Vernacular', era: 'Contemporary', mood: 'charming',
    palette: 'poster-paint primaries — red, blue, yellow, green',
    traits: 'thick poster-paint brushstrokes; naive drawing of subject — sun, flowers, tents; paint drips and uneven coverage; bright primary paint colours; white paper showing through',
    references: 'School fair poster tradition',
    exclude: 'polished digital illustration pretending to be naive, craft-fair AI slop, bunting',
    caution: 'The contrast between naive art and pro type is essential',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'school_cutout_collage', number: 52, name: 'School Cutout Collage',
    category: 'Folk & Vernacular', era: 'Contemporary', mood: 'enthusiastic',
    palette: 'construction paper colours — red, green, blue, yellow, orange',
    traits: 'construction paper in bold colours; visible scissor-cut edges; overlapping paper layers; glue marks or slight misalignment; simple representational shapes — sun, tree, tent',
    references: 'Primary school classroom display',
    exclude: 'digital smoothness, professional polish, stock illustration',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'crayon_folk_handmade', number: 53, name: 'Crayon Folk Handmade',
    category: 'Folk & Vernacular', era: 'Contemporary', mood: 'warm',
    palette: 'crayon primaries with waxy build-up',
    traits: 'waxy crayon texture visible; simple folk motifs — flowers, birds, suns; uneven colour fill; white paper texture showing through; childlike but not incompetent',
    references: 'Folk art tradition, Community hall handmade signs',
    exclude: 'digital craft-fair slop, bunting and pennants, airbrush effects',
    caution: 'Must feel genuinely hand-made, not AI craft-fair slop',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'punk_fanzine', number: 54, name: 'Punk Photocopied Fanzine',
    category: 'Folk & Vernacular', era: '1970s–1980s', mood: 'confrontational',
    palette: 'black, white, photocopy grey, occasional red or yellow xerox',
    traits: 'photocopy grain and high contrast; torn paper edges; ransom-note cut-out letters; black and white with one or two colour xerox layers; safety pins, anarchy symbols, or punk collage',
    references: 'UK punk fanzine tradition, Sniffin\' Glue',
    exclude: 'polished design, craft-fair aesthetics, pastel colours',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'zine_cut_and_paste', number: 55, name: 'Zine Cut-and-Paste',
    category: 'Folk & Vernacular', era: '1990s–present', mood: 'indie',
    palette: 'black and white base with found colour fragments',
    traits: 'cut-out magazine and photo fragments; visible tape or glue marks; margin notes and scribbles; layered transparent overlaps; photocopied texture',
    references: 'Riot grrrl zine tradition, Independent publishing',
    exclude: 'corporate polish, craft-fair warmth, digital flat illustration',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'mexican_calavera', number: 56, name: 'Mexican Calavera / Posada',
    category: 'Folk & Vernacular', era: 'Traditional–present', mood: 'joyful',
    palette: 'vivid saturated warm and cool — orange, yellow, pink, cobalt, purple, black',
    traits: 'skeleton figures in animated poses, dressed and accessorised; decorative papel picado (paper-cut) border patterns; marigold flowers (cempasúchil) as decorative motifs; vivid colour — yellow, orange, pink, purple, cobalt blue; bold outline drawing in woodcut or engraving tradition',
    references: 'José Guadalupe Posada, Day of the Dead tradition, Oaxacan folk art',
    exclude: 'mournful or horror-movie treatment of death, Northern European Halloween aesthetic, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'pennsylvania_dutch', number: 57, name: 'Pennsylvania Dutch / Fraktur',
    category: 'Folk & Vernacular', era: '18th–19th century', mood: 'heraldic',
    palette: 'barn red, cobalt blue, ochre yellow, forest green on cream — flat, no shading',
    traits: 'all animals and figures in strict flat profile or frontal view — no three-quarter angles, no depth; distelfink bird as flat silhouette; bilateral symmetry — paired motifs either side of central axis; internal pattern fills within flat shapes — small dots, chevrons, cross-hatching — not solid colour',
    references: 'Pennsylvania German Fraktur tradition, Taufschein documents, Hex sign tradition',
    exclude: 'naturalistic or three-dimensional animal rendering, cute cartoon animals, soft blended colour or shading, asymmetric composition, modern sans-serif type, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'madhubani', number: 58, name: 'Madhubani / Indian Folk Art',
    category: 'Folk & Vernacular', era: 'Traditional–present', mood: 'devotional',
    palette: 'ochre, terracotta, turmeric yellow, indigo, forest green, black on cream or natural paper',
    traits: 'heavy black outline defining all forms — never open or unfinished; every area filled with geometric pattern — hatching, dots, chevrons; fish, bird, peacock, lotus, and solar motifs; flat colour within pattern fill — no shading or gradient; double black outline borders framing the entire composition',
    references: 'Madhubani painting tradition, Mithila art, Sita Devi',
    exclude: 'empty or negative space, Western perspective or shading, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'scandinavian_rosemaling', number: 59, name: 'Scandinavian Folk / Rosemaling',
    category: 'Folk & Vernacular', era: '18th century–present', mood: 'warm',
    palette: 'earth tones — ochre, sienna, burnt umber; cobalt blue, forest green, rose pink on warm white or cream',
    traits: 'organic flowing acanthus and floral scrollwork; asymmetric composition with visual balance through weight distribution; visible confident brushwork — strokes are expressive, not laboured; rose, tulip, and leaf motifs as primary elements; double-stroke brush technique creating C and S curves',
    references: 'Norwegian Rosemaling tradition, Telemark and Hallingdal regional variants, Swedish folk painting',
    exclude: 'symmetrical or geometric composition, flat vector illustration, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── National & Regional ─────────────────────────────────────────────────────
  {
    id: 'polish_poster_school', number: 60, name: 'Polish Poster School',
    category: 'National & Regional', era: '1950s–1980s', mood: 'artistic',
    palette: 'rich saturated colours — deep red, ultramarine, ochre',
    traits: 'painterly surreal imagery; metaphorical rather than literal representation; bold colour fields; hand-painted texture; sparse composition with single strong image',
    references: 'Henryk Tomaszewski, Jan Lenica, Polish circus poster tradition',
    exclude: 'literal illustration, busy text layout, craft-fair aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'golden_age_travel', number: 61, name: 'Golden Age Travel Poster',
    category: 'National & Regional', era: '1920s–1950s', mood: 'aspirational',
    palette: 'azure blue, warm sand, sunset orange, white',
    traits: 'panoramic landscape or cityscape band; simplified flat colour areas; art deco-influenced lettering; sun, sea, or landmark motif',
    references: 'A.M. Cassandre, British Rail poster tradition, Visit Britain campaigns',
    exclude: 'photographic realism, craft-fair aesthetics, dark moody palettes',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'japanese_minimal_poster', number: 62, name: 'Japanese Minimal Poster',
    category: 'National & Regional', era: '1960s–present', mood: 'calm',
    palette: 'white, black, one muted accent — indigo, vermillion, or moss green',
    traits: 'vast white or single-colour field; one small or medium graphic element; extreme restraint; asymmetrical balance; no decoration',
    references: 'Ikko Tanaka, Japanese graphic design tradition',
    exclude: 'busy illustration, multiple colours, craft-fair decoration, bunting',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'neon_sign', number: 63, name: 'Neon Sign / Diner Americana',
    category: 'National & Regional', era: '1930s–1970s', mood: 'nocturnal',
    palette: 'deep blue-black ground; neon pink, electric blue, acid yellow, warm white glow',
    traits: 'glowing letterforms with a soft neon halo effect on dark background; outlined tube-style lettering suggesting bent glass tubing; dark ground — deep blue-black or near-black; vivid saturated colour — hot pink, electric blue, yellow-white, red; arrows, stars, or simple geometric motifs as secondary neon elements',
    references: 'American neon sign tradition, Las Vegas vintage signage, Route 66 roadside commercial aesthetics, YESCO',
    exclude: 'daylight or bright white backgrounds, flat colour without glow, hand-crafted or artisanal warmth, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'east_german_defa', number: 64, name: 'East German DEFA Film Poster',
    category: 'National & Regional', era: '1950s–1989', mood: 'humanistic',
    palette: 'deep blues, reds, and earthy tones; more varied and warmer than Soviet propaganda',
    traits: 'painterly illustration combining realism with graphic simplification; rich chromatic colour — saturated but not garish; figurative composition with a strong emotional register; faces and gestures carrying psychological weight; layout combining illustration with bold typographic title',
    references: 'DEFA film poster tradition, East German graphic design',
    exclude: 'heroic Soviet propaganda posture, Western commercial gloss, flat vector simplification, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'taisho_modern', number: 65, name: 'Taisho Modern / Japanese 1920s',
    category: 'National & Regional', era: '1912–1926', mood: 'cosmopolitan',
    palette: 'deep red, black, gold, cream; occasional teal or violet',
    traits: 'Art Deco geometry in composition and borders; glamorous figure in hybrid Western-Japanese dress; ukiyo-e-influenced flat colour areas and bold outline; kaisho or mincho calligraphy alongside Roman type; asymmetric composition balancing figure and text',
    references: 'Taisho era commercial poster tradition, Takehisa Yumeji, Japanese department store advertising',
    exclude: 'purely Western Art Deco without Japanese elements, purely traditional Japanese without Western influence, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'shanghai_yuefenpai', number: 66, name: 'Shanghai Calendar Poster / Yuefenpai',
    category: 'National & Regional', era: '1920s–1940s', mood: 'aspirational',
    palette: 'rich saturated warm tones — rouge, jade green, gold, ivory, sky blue',
    traits: 'glamorous female figure in Western or traditional Chinese dress; Western perspective rendering with decorative Chinese elements; saturated colour with careful tonal modelling of figure; product or calendar grid element in corner or border; flowers, fans, or decorative motifs framing the figure',
    references: 'Yuefenpai calendar poster tradition, Hang Zhiying, Republican China commercial art',
    exclude: 'revolutionary or political imagery, austere modernism, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'festival_of_britain', number: 67, name: 'Festival of Britain',
    category: 'National & Regional', era: '1951', mood: 'optimistic',
    palette: 'red, white, blue, gold, bright green — British primary palette with optimism',
    traits: 'molecular or crystalline pattern motifs derived from atomic diagrams; Festival of Britain Britannia or compass-rose symbol; bright primary colours with clean white ground; mid-century modern graphic shapes — atoms, trajectories, abstract forms; playful but precise — science made decorative',
    references: 'Abram Games, Festival Pattern Group, Festival of Britain 1951',
    exclude: 'wartime austerity or drabness, nostalgic tea-cosy Britishness, craft-fair aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'brazilian_tropicalia', number: 68, name: 'Brazilian Tropicália',
    category: 'National & Regional', era: '1967–1972', mood: 'psychedelic',
    palette: 'tropical vivid — hot pink, lime green, orange, turquoise, yellow, deep violet',
    traits: 'tropical imagery — parrots, jungle, carnival — made psychedelic; collage combining photography, colour fields, and drawn elements; bold colour blocks with vibrant clashing combinations; geometric Concretist elements alongside organic tropical forms; pop culture references subverted or celebrated simultaneously',
    references: 'Rogério Duarte, Hélio Oiticica, Tropicália movement, Caetano Veloso album covers',
    exclude: 'pure European graphic design without Brazilian synthesis, austere minimalism, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'italian_futurist_typography', number: 69, name: 'Italian Futurist Typography',
    category: 'National & Regional', era: '1909–1944', mood: 'loud',
    palette: 'black and white principally; occasional red for emphatic words',
    traits: 'words at multiple orientations — diagonal, vertical, radiating; extreme size contrasts between words within a single composition; onomatopoeia as visual object — BANG, RUMBLE, CRASH; white space used dynamically as a field words move through; no conventional reading order',
    references: 'F.T. Marinetti, Zang Tumb Tumb (1914), Italian Futurist typography tradition',
    exclude: 'conventional horizontal reading order, uniform type size or weight, decorative illustration, artificial yellowing or ageing',
    caution: 'Legibility suffers by design — keep essential details in at least one legible cluster',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'soyuzmultfilm', number: 70, name: 'Soyuzmultfilm Animation',
    category: 'National & Regional', era: '1950s–1980s', mood: 'gentle',
    palette: 'dusty ochre, soft forest green, warm grey, muted cobalt, cream — no saturated primaries',
    traits: 'rounded geometric animal characters — circular head, large round eyes, tubular limbs; soft painterly backgrounds with flat colour and subtle texture; muted, sophisticated colour — nothing saturated or primary; simplified landscape or interior settings with Constructivist graphic clarity',
    references: 'Soyuzmultfilm studio, Cheburashka (1966), Nu Pogodi! (1969), Hedgehog in the Fog (1975)',
    exclude: 'American cartoon saturated primaries and hard outlines, Disney or anime character design, aggressive action poses, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'german_expressionist_film', number: 71, name: 'German Expressionist Film Poster',
    category: 'National & Regional', era: '1919–1933', mood: 'threatening',
    palette: 'black, white, and cold grey; occasional deep red as blood accent',
    traits: 'distorted angular architecture with impossible perspective; raking diagonal shadows cutting across the composition; figures made small by overwhelming dark architecture; high contrast — vast black areas with sharp white highlights; tilted or oblique framing creating visual unease',
    references: 'The Cabinet of Dr. Caligari (1920), Nosferatu (1922), Metropolis (1927), Weimar cinema tradition',
    exclude: 'warmth or natural lighting, decorative ornament, artificial yellowing or ageing',
    caution: 'Strong dark mood — excellent for spooky or theatrical events; use carefully for family occasions',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'hong_kong_cinema', number: 72, name: 'Hong Kong Cinema Poster',
    category: 'National & Regional', era: '1950s–1990s', mood: 'kinetic',
    palette: 'vivid warm tones — scarlet, gold, deep orange, cream, sky blue for contrast',
    traits: 'dynamic airbrushed figurative illustration; figures in mid-action — leaping, fighting, shooting — at dramatic angles; vivid warm colour with bold highlights and deep shadows; multiple figures at different scales showing narrative moments; hand-painted title lettering in Chinese and/or Latin script',
    references: 'Hong Kong martial arts cinema poster tradition, Shaw Brothers film posters',
    exclude: 'flat vector illustration, muted or cold colour, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'ukiyo_e_woodblock', number: 73, name: 'Ukiyo-e / Japanese Woodblock',
    category: 'National & Regional', era: '1600s–1900s', mood: 'serene',
    palette: 'indigo, vermillion, black, cream, muted green; woodblock colour range',
    traits: 'bold fluid outlines from a brush-cut keyblock; flat colour areas with no Western-style shading; bokashi — gradated colour within a single area, horizontal bands of lighter-to-darker; strong asymmetric composition; nature or urban scene with distinct foreground/mid/background planes',
    references: 'Katsushika Hokusai, Utagawa Hiroshige, Ukiyo-e print tradition',
    exclude: 'Western Japonisme interpretation (this is the original, not the Western response), gradient shading suggesting Western tonal rendering, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Political & Activist ────────────────────────────────────────────────────
  {
    id: 'wpa_new_deal', number: 74, name: 'WPA / New Deal',
    category: 'Political & Activist', era: '1935–1943', mood: 'civic',
    palette: 'red, blue, cream, black — limited and flat',
    traits: 'silkscreen flat colour areas; bold simplified figures; industrial or community imagery; strong silhouettes',
    references: 'WPA Federal Art Project, Works Progress Administration posters',
    exclude: 'ornate decoration, pastel craft aesthetics, commercial gloss',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'soviet_propaganda', number: 75, name: 'Soviet Propaganda',
    category: 'Political & Activist', era: '1920s–1950s', mood: 'heroic',
    palette: 'red, cream, black, gold',
    traits: 'heroic forward-striding figures; red flag or star motifs; radiating light behind leader figure; bold simplified realism; diagonal upward momentum',
    references: 'Soviet political poster tradition, Gustav Klutsis',
    exclude: 'Western commercial aesthetics, pastel craft palettes, ironic detachment',
    caution: 'Political connotations — frame as historical aesthetic, not endorsement',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'cuban_ospaaal', number: 76, name: 'Cuban OSPAAAL Revolutionary',
    category: 'Political & Activist', era: '1964–1985', mood: 'urgent',
    palette: 'flat black, red, and one other — green, yellow, blue, or orange',
    traits: 'flat bold silhouettes of figures, weapons, or symbols; very limited palette — typically two colours plus black; strong diagonal composition suggesting forward movement; map or globe fragments suggesting international solidarity; large simple shapes with fine detail only in faces or hands',
    references: 'OSPAAAL poster tradition, Alfredo Rostgaard, Rafael Morante',
    exclude: 'photographic realism, decorative ornament, soft gradients, artificial yellowing or ageing',
    caution: 'Political connotations — works best for events with a genuine community or cause dimension',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'atelier_populaire', number: 77, name: 'Atelier Populaire / Paris May 1968',
    category: 'Political & Activist', era: '1968', mood: 'urgent',
    palette: 'black on white; or black on single flat colour — red, yellow, or cream',
    traits: 'large bold black figure or symbol on white or coloured ground; gestural brush-drawn or stencil-cut illustration; one-colour screenprint quality; very simple composition — one image, one slogan; visible roughness of rushed production',
    references: 'Atelier Populaire, Grapus (emerged from same tradition), Mai 68 poster collection',
    exclude: 'polished typesetting, multiple colours, decorative illustration, artificial yellowing or ageing',
    caution: 'Political aesthetic — most effective for events with a genuine collective or community spirit',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'act_up_aids', number: 78, name: 'ACT UP / AIDS Crisis Activism',
    category: 'Political & Activist', era: '1987–1995', mood: 'urgent',
    palette: 'black and one colour — hot pink, red, or white',
    traits: 'stark high-contrast — black ground with white or pink/red graphic element; single powerful symbol — triangle, equals sign, red hand; bold confrontational sans-serif statement; no decorative elements — every mark is functional; activist information hierarchy — demand first, detail below',
    references: 'Gran Fury, ACT UP, Silence=Death Project',
    exclude: 'decorative elements of any kind, warm pastel tones, corporate polish, artificial yellowing or ageing',
    caution: 'Powerful aesthetic — most effective when the event genuinely serves a community need or cause',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'north_korean_propaganda', number: 79, name: 'North Korean Propaganda',
    category: 'Political & Activist', era: '1950s–present', mood: 'triumphalist',
    palette: 'red, white, blue, yellow — all maximally saturated; no muted tones',
    traits: 'heroic forward-striding figures in idealized poses; saturated primary colours — red, blue, yellow — maximally vivid; rising sun, rockets, or crops as background motifs; crowd scenes suggesting mass collective energy; monumental architecture',
    references: 'DPRK poster tradition, North Korean graphic art',
    exclude: 'individualism or ambiguity, muted or pastel colour, ironic tone, artificial yellowing or ageing',
    caution: 'Strong political associations — use the visual language; remove all DPRK-specific iconography',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'soviet_space', number: 80, name: 'Soviet Space Programme',
    category: 'Political & Activist', era: '1957–1975', mood: 'heroic optimism',
    palette: 'deep space blue-black, silver-white, gold-orange rocket flame, red Soviet accent',
    traits: 'rocket silhouette against deep blue-black star field; cosmonaut in pressure suit as heroic figure; orbital trajectory arcs as decorative geometric element; Soviet star motifs and celestial bodies; rays of light emanating from rocket or Earth',
    references: 'Soviet space programme poster tradition, Yuri Gagarin imagery, Constructivist space poster tradition',
    exclude: 'military Cold War imagery, contemporary astronaut aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Music Scenes & Subcultures ─────────────────────────────────────────────
  {
    id: 'rave_acid_graphics', number: 81, name: '90s Rave / Acid Graphics',
    category: 'Music Scenes', era: '1988–1995', mood: 'euphoric',
    palette: 'neon green, hot pink, electric blue, black',
    traits: 'neon colours on black background; warped or melting typography; smiley face or ecstasy-era motifs; fractal or computer-generated backgrounds; dense information listing DJs or acts',
    references: 'Acid house flyer tradition, Designers Republic adjacent',
    exclude: 'craft-fair aesthetics, pastoral imagery, muted palettes',
    caution: 'Use with restraint for family events — keep energy without drug culture references',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'dnb_fractal_flyer', number: 82, name: '90s Drum and Bass Flyer',
    category: 'Music Scenes', era: '1994–1999', mood: 'dark',
    palette: 'black, dark blue, chrome silver, neon green accent',
    traits: 'rendered 3D fractal or chrome objects; dark background with spotlight effects; early CGI aesthetic — dated but deliberate; stacked artist names in small text',
    references: 'Jungle and drum and bass flyer tradition, Early Bryce/Kai\'s Power Tools renders',
    exclude: 'bright pastoral imagery, craft aesthetics, modern flat illustration',
    excludesAging: false, packagePreset: 'pkg_cd_era',
  },
  {
    id: 'festival_indie_lineup', number: 83, name: 'Festival Indie Lineup',
    category: 'Music Scenes', era: '2000s–present', mood: 'contemporary',
    palette: 'depends on central graphic; typically bold and limited',
    traits: 'single strong central illustration or photograph; band names stacked in descending size; sponsor logos in footer band; limited colour palette — two or three colours',
    references: 'Glastonbury poster tradition, Coachella poster format',
    exclude: 'craft-fair aesthetics, bunting, generic spring florals',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'factory_records', number: 84, name: 'Factory Records / Haçienda',
    category: 'Music Scenes', era: '1978–1992', mood: 'austere',
    palette: 'black, white, and one colour — often deep blue, grey, or green',
    traits: 'fine grid with careful proportional relationships; restrained serif/sans dialogue — often a classical face with grotesque; large areas of single colour — black, white, or one strong hue; photography or abstract graphic used with almost no decoration; catalogue numbering as design element',
    references: 'Peter Saville, Factory Records, Haçienda, Joy Division / New Order',
    exclude: 'warmth or decorative detail, clashing colours or busy composition, craft-fair aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'four_ad_records', number: 85, name: '4AD Records',
    category: 'Music Scenes', era: '1980–2000s', mood: 'gothic romantic',
    palette: 'dark — deep red, black, brown, bone white, occasional vivid green or blue',
    traits: 'distressed photographic texture as background or primary image; etched or engraved line drawing overlaid on photography; gothic romantic imagery — architecture, natural forms, decay; dreamlike collage combining incompatible elements at wrong scales; rich dark tones with areas of intense colour',
    references: 'Vaughan Oliver, 23 Envelope, 4AD Records, This Mortal Coil',
    exclude: 'clean Swiss clarity, bright cheerful colours, craft-fair aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'heavy_metal_poster', number: 86, name: 'Heavy Metal Concert Poster',
    category: 'Music Scenes', era: '1970s–present', mood: 'dark',
    palette: 'black, deep red, orange flame, silver, bone white',
    traits: 'skulls, dragons, flames, demons, or fantasy architecture as central image; detailed figurative illustration with dramatic lighting; blackletter or thorned gothic display type; heavy dark background — black or deep purple-black; fire or lightning as compositional elements',
    references: 'Roger Dean (Yes, Asia), Boris Vallejo, Heavy metal album art tradition',
    exclude: 'pastel colours, clean modernist minimalism, ironic detachment, artificial yellowing or ageing',
    caution: 'Tone may not suit all events — excellent for gigs, use with knowing humour for family events',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'reggae_sound_system', number: 87, name: 'Reggae / Sound System Flyer',
    category: 'Music Scenes', era: '1960s–1990s', mood: 'warm',
    palette: 'red, gold, green, black — Rastafarian national colours',
    traits: 'bold hand-lettered or stencil type — no pretence of polish; lion of Judah, map of Africa, or sound system speaker stack as imagery; red, gold, and green as dominant palette with black; event details stacked with emphasis on DJ and sound system names; informal but confident layout',
    references: 'Jamaican sound system tradition, UK dancehall flyer culture, Notting Hill Carnival poster tradition',
    exclude: 'Swiss or corporate polish, cold minimalism, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'hip_hop_flyer', number: 88, name: '80s NYC Hip Hop Flyer',
    category: 'Music Scenes', era: '1979–1995', mood: 'direct',
    palette: 'black, two or three vivid primaries — red, blue, yellow, orange; on white or bright ground',
    traits: 'bubble graffiti lettering for headline or DJ name; break-dancer or b-boy silhouette; colour Xerox or photocopied quality; bold stacked text announcing the event details; boombox, vinyl record, or microphone as secondary motif',
    references: 'Bronx block party flyer tradition, Early hip hop flyer culture',
    exclude: 'corporate polish, Swiss grid, pastel craft aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'blaxploitation', number: 89, name: 'Blaxploitation Movie Poster',
    category: 'Music Scenes', era: '1971–1975', mood: 'cool',
    palette: 'warm 1970s tones — burnt orange, rust, deep brown, cream, electric yellow',
    traits: 'dynamic painted figure in action pose — leaping, shooting, striding; urban cityscape or dramatic background; afro silhouette as a compositional anchor; bold hand-painted title in impactful display lettering; figures at diagonal angles creating energy',
    references: 'Shaft poster (1971), Foxy Brown (1974), Blaxploitation movie poster tradition',
    exclude: 'cold or muted colour, static composition, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'skateboard_graphics', number: 90, name: 'Skateboard / Street Graphics',
    category: 'Music Scenes', era: '1980s–present', mood: 'transgressive',
    palette: 'vivid clashing — hot pink, lime, electric blue, orange, black',
    traits: 'cartoon figures with anatomically wrong or exaggerated features; skulls, eyeballs, dripping elements, claws, or screaming mouths; heavy black outline on all forms; vivid, saturated colours — often clashing; psychedelic or surreal background elements',
    references: 'Jim Phillips (Santa Cruz), VCJ — Vernon Courtlandt Johnson, Pushead — Brian Schroeder, Thrasher magazine',
    exclude: 'clean corporate illustration, pastel or muted colours, conventional poster hierarchy, artificial yellowing or ageing',
    caution: 'May not suit conservative audiences — excellent for youth-focused or alternative events',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },

  // ─── Digital & Screen ────────────────────────────────────────────────────────
  {
    id: 'designers_republic', number: 91, name: 'Designers Republic',
    category: 'Digital & Screen', era: '1986–present', mood: 'subversive',
    palette: 'white, black, red, yellow, industrial grey',
    traits: 'bold geometric logo-like forms; consumer product parody; techno-industrial aesthetic; layered text slogans; Japanese-influenced flat colour',
    references: 'Designers Republic, Ian Anderson, Warp Records',
    exclude: 'craft-fair aesthetics, pastoral imagery, conventional event poster layout',
    caution: 'AI may add extra marketing copy unprompted — specify exact text',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'nasa_worm_modernist', number: 92, name: 'NASA Worm Modernist',
    category: 'Digital & Screen', era: '1975–1992', mood: 'technical',
    palette: 'warm red, warm grey, white, black',
    traits: 'clean vector forms; systematic colour coding; technical diagram aesthetic; bold logotype integration; grid-precise layout',
    references: 'NASA Graphics Standards Manual, Danne & Blackburn',
    exclude: 'hand-drawn elements, decorative ornament, craft aesthetics',
    excludesAging: false, packagePreset: 'pkg_vinyl_80s',
  },
  {
    id: 'psychedelic_seventies_corporate', number: 93, name: 'Psychedelic Seventies Corporate',
    category: 'Digital & Screen', era: '1968–1979', mood: 'groovy-but-wholesome',
    palette: 'burnt orange, avocado, harvest gold, brown, cream',
    traits: 'soft rainbow gradients; rounded bubble forms; friendly illustration style; warm earth-tone overlays; slightly faded print quality',
    references: '1970s corporate branding, Yellow Submarine mainstream influence',
    exclude: 'underground punk aesthetic, dark neon rave style, craft-fair slop',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'y2k_chrome', number: 94, name: 'Y2K Chrome',
    category: 'Digital & Screen', era: '1998–2004', mood: 'optimistic',
    palette: 'silver, electric blue, hot pink, white, iridescent',
    traits: 'chrome and holographic gradients; translucent blob forms; star sparkle accents; early 3D render aesthetic; iridescent colour shifts',
    references: 'Y2K aesthetic revival, Early internet era design',
    exclude: 'flat minimalism, craft aesthetics, muted natural palettes',
    excludesAging: false, packagePreset: 'pkg_cd_era',
  },
  {
    id: 'contemporary_flat_illustration', number: 95, name: 'Contemporary Flat Illustration',
    category: 'Digital & Screen', era: '2015–present', mood: 'contemporary',
    palette: 'bold but not pastel — coral, teal, navy, mustard',
    traits: 'flat vector shapes with no gradient; limited palette — three or four colours; geometric simplification of figures and objects; subtle texture overlay optional',
    references: 'Contemporary tech company illustration style, Editorial vector art',
    exclude: 'pastel craft-fair palette, bunting and pennants, hand-drawn twee florals, airbrush effects',
    caution: 'Highest slop risk — must specify what NOT to do using the exclude list',
    excludesAging: false, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'vaporwave', number: 96, name: 'Vaporwave',
    category: 'Digital & Screen', era: '2010–present', mood: 'nostalgic',
    palette: 'pastel neon — cyan, lavender, hot pink, peach on deep purple or dark blue',
    traits: 'classical marble bust or column in neon-lit grid environment; Windows 95 or early Macintosh UI element fragments; Japanese katakana text as decorative visual element; horizontal scan lines over photographic areas; perspective grid receding to a glowing horizon',
    references: 'Vaporwave aesthetic, Macintosh Plus — Floral Shoppe, Early internet aesthetic tradition',
    exclude: 'warm organic colours, hand-made or craft textures, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'synthwave', number: 97, name: 'Synthwave / Retrowave',
    category: 'Digital & Screen', era: '2010–present', mood: 'nostalgic',
    palette: 'deep purple-black background; hot pink, electric blue, cyan, and gold neon',
    traits: 'perspective grid receding to a bright horizon line; gradient sunset — hot pink fading to deep purple; chrome or metallic 3D letterforms; palm tree silhouettes against the gradient sky; horizontal scan lines suggesting a CRT monitor',
    references: 'Synthwave music aesthetic, Outrun video game aesthetic, Miami Vice visual tradition',
    exclude: 'hand-made or organic textures, natural colour palettes, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'glitch_art', number: 98, name: 'Glitch Art',
    category: 'Digital & Screen', era: '2005–present', mood: 'uncanny',
    palette: 'base image palette fragmented; RGB shift adds cyan, magenta, yellow fringing',
    traits: 'RGB channel separation — red, green, blue shifts out of alignment; pixel sorting creating streaked columns or rows of colour; scan-line or interlace artifacts suggesting video signal failure; recognisable subject partially surviving data corruption; sharp horizontal banding cutting across imagery',
    references: 'Glitch art tradition, Rosa Menkman, Databending and pixel sorting',
    exclude: 'analogue grain or film texture — glitch is digital not analog, warm craft aesthetics, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'pixel_art', number: 99, name: 'Pixel Art',
    category: 'Digital & Screen', era: '1978–present', mood: 'nostalgic',
    palette: 'hardware-specific — CGA (cyan/magenta/white/black), Spectrum (pure primaries + black/white), NES, or Game Boy (four greens)',
    traits: 'large square pixels visible as discrete units — chunky, not smooth; strictly limited colour palette — 4 to 16 colours maximum; dithering patterns — checkerboard alternation — to suggest additional tones; sprite-based character design; tile-based backgrounds with visible repetition',
    references: 'Arcade game aesthetics — Pac-Man, Space Invaders, Donkey Kong, ZX Spectrum, Commodore 64, NES, Game Boy',
    exclude: 'smooth anti-aliased edges, gradients or soft colour blending, modern high-resolution illustration, artificial yellowing or ageing',
    caution: 'Specify the hardware palette by name (CGA, Spectrum, Game Boy) for a specific look',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'low_poly', number: 100, name: 'Low Poly',
    category: 'Digital & Screen', era: '1994–present', mood: 'uncanny',
    palette: 'flat, unshaded polygon colours — vivid but matte; no metallic or iridescent tones',
    traits: 'each polygon face is a single flat, completely unshaded colour — no highlight, no gradient within a face; zero reflections or specularity on any surface — fully matte throughout; textures visibly pixelated at 64×64 resolution; hard, unconvincing shadows; distance fog as an abrupt flat colour cutoff, not a smooth gradient',
    references: 'PlayStation 1 and Nintendo 64 game aesthetics, Tomb Raider (1996), Final Fantasy VII (1997), Crash Bandicoot (1996)',
    exclude: 'specular highlights or reflections of any kind, ambient occlusion or global illumination, smooth gradient fog, modern PBR materials, artificial yellowing or ageing',
    caution: 'If output looks too polished, add "flat shading only, no specular, no reflections, no ambient occlusion, matte surfaces, PS1-era graphics"',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
  {
    id: 'golden_hour_photoreal', number: 101, name: 'Golden Hour Photo-Real',
    category: 'Digital & Screen', era: '1972–1979 (house original)', mood: 'pastoral',
    palette: 'amber, ochre, cream, faded denim blue, burnt sienna',
    traits: 'warm golden-hour backlighting with soft lens flare; shallow depth of field from medium format film; hazy, sun-faded late-afternoon atmosphere; natural outdoor or rural setting — field, fence line, porch, tree line; quiet, unposed character moment rather than a performance pose',
    references: '1970s soft rock and singer-songwriter album photography, Kodachrome and Ektachrome color rendering, medium format film portraiture',
    exclude: 'illustration or vector rendering, flat graphic-design treatment, digital gloss or oversaturation, modern studio lighting, harsh flash, seamless studio backdrop, glossy CGI sheen',
    caution: 'This is the one photographic entry — if output drifts toward painterly or vector-illustration territory, reinforce explicitly: "photograph, not illustration or digital art"',
    excludesAging: false, packagePreset: 'pkg_vinyl_classic',
  },
  {
    id: 'fmr_records', number: 102, name: 'FMR Records',
    category: 'Music Scenes', era: '2020s (house original)', mood: 'warm analog',
    palette: 'terracotta, cream, warm charcoal, brass gold accent',
    traits: 'warm analog studio aesthetic — tape saturation grain, hand-set catalog typography, a single confident terracotta accent against a warm neutral ground; tactile paper or cardboard texture; restrained modernist layout with generous negative space; a simple house mark or monogram as the only ornament',
    references: 'FMR Records house style — modern analog-warmth revival, minimalist contemporary indie-label sleeve design, warm vinyl reissue packaging',
    exclude: 'cold digital gloss, neon or synthetic colours, cluttered or maximalist layout, corporate stock-photo feel, artificial yellowing or ageing',
    excludesAging: true, packagePreset: 'pkg_modern_print',
  },
];

export interface StylePresetLike {
  name: string;
  description: string;
}

/**
 * Synthesize an atlas-shaped StyleEntry from a saved Style Preset, so every
 * preset the user has under Presets gets a matching album-cover style option
 * without needing its own hand-authored atlas entry or any DB sync — this
 * runs live off whatever presets are currently fetched, so it can never go
 * stale. Distinguished from the curated 1-102 catalog via the "Style
 * Presets" category and a synthetic id/number namespace.
 */
export function buildPresetAtlasEntry(preset: StylePresetLike, index: number): StyleEntry {
  return {
    id: `preset_${preset.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`,
    number: 1000 + index,
    name: preset.name,
    category: 'Style Presets',
    era: 'Your Preset',
    mood: 'custom',
    palette: 'derived from your production blueprint — no fixed palette',
    traits: preset.description,
    references: `Style Preset "${preset.name}" from your HookHouse Pro Presets`,
    exclude: 'clashing or unrelated genre imagery',
    excludesAging: false,
    packagePreset: 'pkg_modern_print',
  };
}

export interface AtlasPromptOpts {
  title?: string;
  artist?: string;
  summary?: string;
  /** First ~300 chars of lyrics used to anchor the emotional/visual scene */
  lyricsExcerpt?: string;
}

/**
 * Build a complete, self-contained GPT-Image-2 prompt for an album cover.
 * Natural language paragraphs — no Prompt Architect format, no character injection.
 */
export function buildAtlasPrompt(
  style: StyleEntry,
  preset: PackagePreset,
  opts: AtlasPromptOpts = {},
  aspectRatio: string = '1:1'
): string {
  const p = PACKAGE_PRESETS[preset];
  const { title, artist, summary, lyricsExcerpt } = opts;

  const parts: string[] = [];

  // Opening: what this is
  const songId = title
    ? `"${title}"${artist ? ` by ${artist}` : ''}`
    : 'an original song';
  // The ratio must lead — without it the image model treats it as a frame to
  // place a cover inside, rather than rendering the image itself at that ratio.
  parts.push(`${aspectRatio} Album Cover Artwork for ${songId}.`);

  // Emotional/scene setup from song content
  if (summary) {
    parts.push(`The song's feeling and subject: ${summary.trim()}`);
  }
  if (lyricsExcerpt) {
    parts.push(`Lyrical tone to draw from: "${lyricsExcerpt.trim()}"`);
  }

  // Visual style
  parts.push(
    `Visual style: ${style.name} (${style.era}). ${style.traits.trim()} ` +
    `Mood: ${style.mood}. Inspired by: ${style.references}.`
  );

  // Colour palette
  parts.push(`Color palette: ${style.palette}.`);

  // Physical packaging
  parts.push(
    `Physical presentation: ${p.clause}. ` +
    `${p.labelImprint}. ` +
    `${PHYSICAL_EDGE_CLAUSE}.`
  );

  // Hard format
  parts.push(`${aspectRatio} aspect ratio. High resolution, print-quality render.`);

  // Negative
  parts.push(`Do not include: ${style.exclude}.`);

  if (style.caution) {
    parts.push(`Note: ${style.caution}`);
  }

  return parts.join('\n\n');
}

/** Find all styles that match a house persona by ID (primary + alternates) */
export function getPersonaStyles(persona: string): StyleEntry[] {
  const def = HOUSE_DEFAULTS[persona];
  if (!def) return [];
  const ids = new Set([def.primary, ...def.alternates]);
  return IMAGE_STYLE_ATLAS.filter(s => ids.has(s.id));
}

/** Auto-suggest a style ID from song summary/genre text */
export function autoSuggestStyle(text: string): string | null {
  const lower = text.toLowerCase();
  for (const [persona, def] of Object.entries(HOUSE_DEFAULTS)) {
    const keywords = persona.toLowerCase().split(/[\s/]+/);
    if (keywords.some(kw => lower.includes(kw))) return def.primary;
  }
  return null;
}
